import { api, setAccessToken, setRefreshToken } from '@/lib/api';

/**
 * The auth endpoints, typed.
 *
 * Kept separate from the screens so the login form does not have to know that
 * `POST /auth/login` can return three different shapes.
 */

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface MfaChallengeRequired {
  mfaRequired: true;
  challengeToken: string;
  attemptsRemaining: number;
  recoveryCodesRemaining: number;
}

export interface MfaEnrolmentRequired {
  mfaEnrolmentRequired: true;
  enrolmentToken: string;
}

export type LoginResult = TokenPair | MfaChallengeRequired | MfaEnrolmentRequired;

export function isMfaChallenge(result: LoginResult): result is MfaChallengeRequired {
  return 'mfaRequired' in result;
}

export function isMfaEnrolment(result: LoginResult): result is MfaEnrolmentRequired {
  return 'mfaEnrolmentRequired' in result;
}

/** Records a fresh pair. The access token stays in memory; see `lib/api.ts`. */
export function adoptTokens(tokens: TokenPair): void {
  setAccessToken(tokens.accessToken);
  setRefreshToken(tokens.refreshToken);
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResult>('/auth/login', { email, password }),

  mfaChallenge: (challengeToken: string, input: { code?: string; recoveryCode?: string }) =>
    api.post<TokenPair>('/auth/mfa/challenge', { challengeToken, ...input }),

  forcedEnrolment: (enrolmentToken: string, code: string) =>
    api.post<TokenPair & { recoveryCodes: string[] }>('/auth/mfa/enrol/forced', {
      enrolmentToken,
      code,
    }),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, newPassword }),

  /**
   * Consumes the token. Single-use and atomic server-side, so calling this
   * twice fails the second time - which is why the screen only sends it on a
   * deliberate click. See VerifyEmailScreen.
   */
  verifyEmail: (token: string) => api.post<{ verified: boolean }>('/auth/verify-email', { token }),

  /** Always resolves, whether or not the address exists or is already verified. */
  resendVerification: (email: string) => api.post<void>('/auth/resend-verification', { email }),

  session: () =>
    api.get<{
      fresh: boolean;
      secondsRemaining: number | null;
      warnAtSeconds: number;
      idleTimeoutMinutes: number;
      authAt: number | null;
    }>('/auth/session'),

  heartbeat: () => api.post<void>('/auth/session/heartbeat'),
};
